import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth, apiPortraitUrl } from "@/auth";

/* ============================================================
 /inbox — PvP challenges (incoming + outgoing)
 ============================================================ */

const STATUS_COPY = {
 pending: { label: "AWAITING RESPONSE", color: "text-yt-yellow" },
 scheduled: { label: "SCHEDULED", color: "text-yt-yellow" },
 responded: { label: "STITCHING AUDIO", color: "text-yt-yellow" },
 judging: { label: "JUDGING", color: "text-yt-yellow" },
 completed: { label: "COMPLETED", color: "text-yt-judge" },
 declined: { label: "DECLINED", color: "text-yt-muted" },
 expired: { label: "EXPIRED", color: "text-yt-muted" },
};

function formatCountdown(iso) {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  const now = Date.now();
  const diff = target - now;
  if (diff <= 0) return "DUE NOW";
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function Inbox() {
 const { user } = useAuth();
 const [data, setData] = useState({ incoming: [], outgoing: [] });
 const [tab, setTab] = useState("incoming");

 useEffect(() => {
 if (!user) return;
 const load = () => axios.get(`${API}/challenges/mine`).then(r => setData(r.data)).catch(() => {});
 load();
 const iv = setInterval(load, 15000);
 return () => clearInterval(iv);
 }, [user]);

 if (!user) {
 return (
 <div className="max-w-3xl mx-auto px-6 py-24 text-center">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-4"> SIGN IN REQUIRED</div>
 <h1 className="font-display text-4xl md:text-5xl mb-6">CHALLENGE INBOX</h1>
 <Link to="/login" className="inline-block bg-yt-yellow text-black font-display text-xl px-6 py-3 tracking-wider shadow-brutal">
 SIGN IN
 </Link>
 </div>
 );
 }

 const list = tab === "incoming" ? data.incoming : data.outgoing;
 const unread = data.incoming.filter(c => c.status === "pending" || c.status === "scheduled").length;

 return (
 <div className="max-w-5xl mx-auto px-6 md:px-10 py-10">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-2"> CHALLENGE INBOX</div>
 <h1 className="font-display text-5xl md:text-7xl tracking-tight mb-8">
 YOUR <span className="text-yt-red">CHALLENGES</span>
 </h1>

 <div className="flex gap-2 mb-6">
 <button data-testid="inbox-tab-incoming" onClick={() => setTab("incoming")}
 className={`px-4 py-2 font-mono text-xs tracking-widest border-2 transition-colors ${
 tab === "incoming" ? "border-yt-yellow bg-yt-yellow text-black" : "border-yt-border text-white hover:border-yt-yellow"
 }`}>
 INCOMING {unread > 0 && <span className="ml-1 bg-yt-red text-white px-1">{unread}</span>}
 </button>
 <button data-testid="inbox-tab-outgoing" onClick={() => setTab("outgoing")}
 className={`px-4 py-2 font-mono text-xs tracking-widest border-2 transition-colors ${
 tab === "outgoing" ? "border-yt-yellow bg-yt-yellow text-black" : "border-yt-border text-white hover:border-yt-yellow"
 }`}>
 OUTGOING
 </button>
 </div>

 <div data-testid="inbox-list" className="space-y-3">
 {list.length === 0 ? (
 <div className="text-center py-16 font-mono text-yt-muted">
 {tab === "incoming"
 ? "No challenges waiting for you. Yet."
 : "You haven't challenged anyone yet."}
 <div className="mt-6">
 <Link to="/rappers" className="inline-block bg-yt-yellow text-black font-display text-lg tracking-wider px-6 py-3 shadow-brutal">
 {tab === "incoming" ? "▶ FIND A CHALLENGER" : "▶ FIND A RAPPER"}
 </Link>
 </div>
 </div>
 ) : list.map(c => {
 const other = tab === "incoming" ? c.challenger_name : c.challenged_name;
 const otherPortrait = tab === "incoming" ? c.challenger_portrait : c.challenged_portrait;
 const st = STATUS_COPY[c.status] || { label: c.status.toUpperCase(), color: "text-white" };
 return (
 <Link key={c.id} to={`/pvp/${c.id}`} data-testid={`inbox-item-${c.id}`}
 className="block bg-yt-surface border-2 border-yt-border hover:border-yt-yellow transition-colors p-4">
 <div className="flex items-center gap-4">
 <div className="w-16 h-20 bg-black border border-yt-border flex-shrink-0 overflow-hidden">
 {otherPortrait ? (
 <img src={apiPortraitUrl(otherPortrait)} alt={other} className="w-full h-full object-cover" />
 ) : (
 <div className="w-full h-full flex items-center justify-center bg-yt-surface font-display text-lg text-yt-yellow">{(c.creator_name || "??").slice(0, 2).toUpperCase()}</div>
 )}
 </div>
 <div className="flex-1 min-w-0">
 <div className="font-mono text-[10px] tracking-widest text-yt-muted mb-1">
 {tab === "incoming" ? "FROM" : "TO"}
 </div>
 <div className="font-display text-2xl text-white truncate">{other}</div>
 <div className="font-mono text-xs text-yt-yellow truncate">
 {c.arena_name?.toUpperCase()}
 </div>
 {c.trash_talk && (
 <div className="mt-1 font-mono text-xs text-yt-muted italic truncate">
 &ldquo;{c.trash_talk}&rdquo;
 </div>
 )}
 </div>
 <div className="text-right flex-shrink-0">
 <div className={`font-mono text-xs tracking-widest ${st.color}`}>{st.label}</div>
 {c.status === "scheduled" && c.scheduled_at && (
 <div className="mt-1 font-mono text-[10px] text-yt-yellow">
 IN {formatCountdown(c.scheduled_at)}
 </div>
 )}
 {c.status === "completed" && (
 <div className="mt-1 font-display text-sm">
 {c.winner_name === user.rapper_name
 ? <span className="text-yt-judge">★ YOU WON</span>
 : <span className="text-yt-red">LOST</span>}
 </div>
 )}
 {c.status === "pending" && tab === "incoming" && (
 <div className="mt-1 font-mono text-[10px] text-yt-red">
 RESPOND ▶
 </div>
 )}
 {c.status === "scheduled" && tab === "incoming" && (
 <div className="mt-1 font-mono text-[10px] text-yt-red">
 DROP BARS ▶
 </div>
 )}
 </div>
 </div>
 </Link>
 );
 })}
 </div>
 </div>
 );
}
